import { useEffect, useState, useRef } from "react";
import { Loader2, Eye, ArrowLeft, RotateCcw, CalendarCheck, ArrowDownUp, LayoutGrid, List, GripVertical, MapPin } from "lucide-react";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { listQuotes, getQuote, refundDeposit, adminLogin, ADMIN_TOKEN_KEY, updateLeadStatus, listShops, setShopStatus } from "@/lib/api";
import { formatPrice, SERVICES, LEAD_STATUSES } from "@/lib/constants";

const serviceLabel = (key) =>
    SERVICES.find((s) => s.key === key)?.label || key;

const LEAD_BADGE_STYLES = {
    new: "text-sky-300 border-sky-500/40 bg-sky-500/10",
    matched: "text-amber-300 border-amber-500/40 bg-amber-500/10",
    completed: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
};
const leadLabel = (key) =>
    LEAD_STATUSES.find((s) => s.key === key)?.label || "New";

const KANBAN_COLUMN_ACCENT = {
    new: "border-t-sky-500/70",
    matched: "border-t-amber-500/70",
    completed: "border-t-emerald-500/70",
};

const KanbanCard = ({ q, onView, onDragStart, onDragEnd, dragging }) => (
    <div
        data-testid={`kanban-card-${q.id}`}
        draggable
        onDragStart={(e) => onDragStart(e, q.id)}
        onDragEnd={onDragEnd}
        className={`group rounded-xl border border-navy bg-navy/60 p-3.5 cursor-grab active:cursor-grabbing transition-all hover:border-safety/50 ${
            dragging ? "opacity-40" : "opacity-100"
        }`}
    >
        <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
                <div className="text-slate-100 font-medium truncate">
                    {q.full_name}
                </div>
                <div className="text-slate-400 text-xs font-light truncate">
                    {q.vehicle_year || ""} {q.vehicle_make} {q.vehicle_model}
                    <span className="capitalize"> · {q.vehicle_type}</span>
                </div>
            </div>
            <GripVertical
                size={16}
                className="text-slate-600 shrink-0 mt-0.5 group-hover:text-slate-400"
            />
        </div>
        <div className="mt-2.5 text-xs text-slate-300 font-light line-clamp-2">
            {q.services.map(serviceLabel).join(", ")}
        </div>
        <div className="mt-2.5 text-safety text-sm font-medium">
            {formatPrice(q.ai_estimate_low || q.estimate_low)}
            <span className="text-slate-600"> – </span>
            {formatPrice(q.ai_estimate_high || q.estimate_high)}
        </div>
        <div className="mt-3 flex items-center justify-between gap-2">
            {(q.suburb || q.region) && (
                <div className="flex items-center gap-1 text-slate-400 text-xs min-w-0">
                    <MapPin size={12} className="shrink-0" />
                    <span className="truncate">
                        {[q.suburb, q.region].filter(Boolean).join(", ")}
                    </span>
                </div>
            )}
            <button
                data-testid={`kanban-view-${q.id}`}
                onClick={() => onView(q.id)}
                className="ml-auto inline-flex items-center gap-1 text-xs uppercase tracking-[0.12em] text-slate-400 hover:text-safety transition-colors shrink-0"
            >
                <Eye size={14} /> View
            </button>
        </div>
    </div>
);

export default function AdminPage() {
    const [authed, setAuthed] = useState(false);
    const [pwd, setPwd] = useState("");
    const [loggingIn, setLoggingIn] = useState(false);
    const [loading, setLoading] = useState(false);
    const [quotes, setQuotes] = useState([]);
    const [active, setActive] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [refunding, setRefunding] = useState(false);
    const [savingStatus, setSavingStatus] = useState(false);
    const [statusFilter, setStatusFilter] = useState("all");
    const [sortOrder, setSortOrder] = useState("newest");
    const [view, setView] = useState("table");
    const [section, setSection] = useState("leads");
    const [draggedId, setDraggedId] = useState(null);
    const [dragOverCol, setDragOverCol] = useState(null);
    const draggedIdRef = useRef(null);

    useEffect(() => {
        if (sessionStorage.getItem(ADMIN_TOKEN_KEY)) setAuthed(true);
    }, []);

    const logout = () => {
        sessionStorage.removeItem(ADMIN_TOKEN_KEY);
        setAuthed(false);
        setQuotes([]);
    };

    const login = async (e) => {
        e?.preventDefault();
        setLoggingIn(true);
        try {
            const { token } = await adminLogin(pwd);
            sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
            setPwd("");
            setAuthed(true);
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Incorrect password.",
            );
        } finally {
            setLoggingIn(false);
        }
    };

    const handleAuthError = (e) => {
        if (e?.response?.status === 401) {
            toast.error("Session expired — please log in again.");
            logout();
            return true;
        }
        return false;
    };

    const load = async () => {
        setLoading(true);
        try {
            const data = await listQuotes();
            setQuotes(data);
        } catch (e) {
            if (!handleAuthError(e)) toast.error("Could not load quotes.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (authed) load();
    }, [authed]);

    const openDetail = async (id) => {
        setDetailLoading(true);
        setActive({ id });
        try {
            const full = await getQuote(id);
            setActive(full);
        } catch (e) {
            if (!handleAuthError(e)) toast.error("Could not load quote details.");
            setActive(null);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleRefund = async () => {
        if (!active?.id) return;
        if (
            !window.confirm(
                "Refund this customer's NZ$50 deposit? This cannot be undone.",
            )
        )
            return;
        setRefunding(true);
        try {
            await refundDeposit(active.id);
            toast.success("Deposit refunded.");
            setActive((a) => ({
                ...a,
                deposit_paid: false,
                status: "refunded",
                dropoff_date: null,
            }));
            load();
        } catch (e) {
            if (!handleAuthError(e))
                toast.error(
                    e?.response?.data?.detail || "Refund failed. Please try again.",
                );
        } finally {
            setRefunding(false);
        }
    };

    const moveLead = async (id, next) => {
        const current = quotes.find((q) => q.id === id);
        if (!current || (current.lead_status || "new") === next) return;
        const prev = current.lead_status || "new";
        // optimistic update
        setQuotes((qs) =>
            qs.map((q) => (q.id === id ? { ...q, lead_status: next } : q)),
        );
        setActive((a) => (a && a.id === id ? { ...a, lead_status: next } : a));
        try {
            await updateLeadStatus(id, next);
            toast.success(`Lead marked ${leadLabel(next)}.`);
        } catch (e) {
            // revert on failure
            setQuotes((qs) =>
                qs.map((q) => (q.id === id ? { ...q, lead_status: prev } : q)),
            );
            setActive((a) => (a && a.id === id ? { ...a, lead_status: prev } : a));
            if (!handleAuthError(e))
                toast.error(
                    e?.response?.data?.detail || "Could not update status.",
                );
        }
    };

    const changeLeadStatus = async (next) => {
        if (!active?.id) return;
        setSavingStatus(true);
        await moveLead(active.id, next);
        setSavingStatus(false);
    };

    const handleDragStart = (e, id) => {
        draggedIdRef.current = id;
        setDraggedId(id);
        e.dataTransfer.effectAllowed = "move";
    };

    const handleDragEnd = () => {
        draggedIdRef.current = null;
        setDraggedId(null);
        setDragOverCol(null);
    };

    const handleColumnDrop = (status) => {
        const id = draggedIdRef.current;
        setDragOverCol(null);
        if (id) moveLead(id, status);
        draggedIdRef.current = null;
        setDraggedId(null);
    };

    if (!authed) {
        return (
            <div className="min-h-screen bg-navy grid place-items-center px-6">
                <form
                    onSubmit={login}
                    data-testid="admin-login-form"
                    className="w-full max-w-sm bg-navy border border-navy p-8"
                >
                    <a
                        href="/"
                        className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-safety mb-6"
                    >
                        <ArrowLeft size={14} /> Back to site
                    </a>
                    <h1 className="font-display text-3xl text-lightgrey mb-2">
                        Admin Access
                    </h1>
                    <p className="text-slate-400 text-sm mb-6 font-light">
                        Enter the staff password to view submitted quotes.
                    </p>
                    <input
                        type="password"
                        value={pwd}
                        onChange={(e) => setPwd(e.target.value)}
                        placeholder="Password"
                        data-testid="admin-password-input"
                        autoFocus
                        className="w-full h-12 bg-navy border border-navy focus:border-safety focus:outline-none text-slate-100 placeholder:text-slate-600 px-4 text-sm"
                    />
                    <button
                        type="submit"
                        disabled={loggingIn}
                        data-testid="admin-login-submit"
                        className="w-full mt-4 h-12 bg-safety hover:bg-safety disabled:opacity-50 text-navy text-sm uppercase tracking-[0.2em] transition-colors"
                    >
                        {loggingIn ? "Checking…" : "Enter"}
                    </button>
                </form>
            </div>
        );
    }

    const statusCounts = quotes.reduce(
        (acc, q) => {
            const k = q.lead_status || "new";
            acc[k] = (acc[k] || 0) + 1;
            return acc;
        },
        { new: 0, matched: 0, completed: 0 },
    );
    const visibleQuotes = quotes
        .filter(
            (q) =>
                statusFilter === "all" ||
                (q.lead_status || "new") === statusFilter,
        )
        .sort((a, b) => {
            const da = new Date(a.created_at).getTime();
            const db2 = new Date(b.created_at).getTime();
            return sortOrder === "newest" ? db2 - da : da - db2;
        });

    const sortedQuotes = [...quotes].sort((a, b) => {
        const da = new Date(a.created_at).getTime();
        const db2 = new Date(b.created_at).getTime();
        return sortOrder === "newest" ? db2 - da : da - db2;
    });

    return (
        <div className="min-h-screen bg-navy text-slate-100">
            <header className="border-b border-navy bg-navy/80 backdrop-blur sticky top-0 z-10">
                <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
                    <div className="flex items-center gap-6">
                        <a href="/" className="font-display text-2xl">
                            Panel <span className="text-safety">Pros</span>
                            <span className="text-slate-500 text-sm font-body ml-3 tracking-[0.2em] uppercase">
                                Admin
                            </span>
                        </a>
                    </div>
                    <button
                        onClick={logout}
                        data-testid="admin-logout"
                        className="text-xs uppercase tracking-[0.2em] text-slate-400 hover:text-safety"
                    >
                        Log out
                    </button>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 md:px-10 py-12">
                <div className="flex gap-2 mb-8">
                    {[["leads", "Leads"], ["shops", "Panel Shops"]].map(([k, label]) => (
                        <button key={k} data-testid={`admin-section-${k}`} onClick={() => setSection(k)}
                            className={`px-4 py-2 rounded-xl text-xs uppercase tracking-[0.15em] transition-colors ${section === k ? "bg-safety text-white font-semibold" : "text-slate-300 border border-slate-700 hover:border-safety/60"}`}>
                            {label}
                        </button>
                    ))}
                </div>
                {section === "shops" && <ShopsPanel />}
                {section === "leads" && (<>
                <div className="flex items-end justify-between mb-8">
                    <div>
                        <h1 className="font-display text-4xl md:text-5xl font-light">
                            Quote Submissions
                        </h1>
                        <p className="text-slate-400 mt-2 font-light">
                            {quotes.length}{" "}
                            {quotes.length === 1 ? "quote" : "quotes"} on file.
                        </p>
                    </div>
                    <button
                        onClick={load}
                        data-testid="admin-refresh"
                        className="text-xs uppercase tracking-[0.2em] text-slate-400 hover:text-safety border-b border-slate-700 hover:border-safety pb-1"
                    >
                        Refresh
                    </button>
                </div>

                {quotes.length > 0 && (
                    <div
                        data-testid="admin-filter-bar"
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6"
                    >
                        <div className="flex flex-wrap items-center gap-2">
                            <div className="inline-flex rounded-xl border border-navy overflow-hidden mr-1">
                                <button
                                    data-testid="admin-view-table"
                                    onClick={() => setView("table")}
                                    className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs uppercase tracking-[0.15em] transition-colors ${
                                        view === "table"
                                            ? "bg-safety text-navy font-semibold"
                                            : "text-slate-300 hover:bg-navy/60"
                                    }`}
                                >
                                    <List size={14} /> Table
                                </button>
                                <button
                                    data-testid="admin-view-kanban"
                                    onClick={() => setView("kanban")}
                                    className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs uppercase tracking-[0.15em] transition-colors ${
                                        view === "kanban"
                                            ? "bg-safety text-navy font-semibold"
                                            : "text-slate-300 hover:bg-navy/60"
                                    }`}
                                >
                                    <LayoutGrid size={14} /> Board
                                </button>
                            </div>
                            {view === "table" && [
                                { key: "all", label: "All", count: quotes.length },
                                { key: "new", label: "New", count: statusCounts.new },
                                { key: "matched", label: "Matched", count: statusCounts.matched },
                                { key: "completed", label: "Completed", count: statusCounts.completed },
                            ].map((f) => (
                                <button
                                    key={f.key}
                                    data-testid={`admin-filter-${f.key}`}
                                    onClick={() => setStatusFilter(f.key)}
                                    className={`px-3.5 py-1.5 text-xs uppercase tracking-[0.15em] rounded-xl border transition-colors ${
                                        statusFilter === f.key
                                            ? "bg-safety text-navy border-safety font-semibold"
                                            : "text-slate-300 border-navy hover:border-safety/60"
                                    }`}
                                >
                                    {f.label}
                                    <span className="ml-1.5 opacity-70">{f.count}</span>
                                </button>
                            ))}
                        </div>
                        <button
                            data-testid="admin-sort-toggle"
                            onClick={() =>
                                setSortOrder((s) => (s === "newest" ? "oldest" : "newest"))
                            }
                            className="inline-flex items-center gap-2 px-3.5 py-1.5 text-xs uppercase tracking-[0.15em] text-slate-300 border border-navy hover:border-safety/60 rounded-xl transition-colors self-start sm:self-auto"
                        >
                            <ArrowDownUp size={14} />
                            {sortOrder === "newest" ? "Newest first" : "Oldest first"}
                        </button>
                    </div>
                )}

                {loading ? (
                    <div className="py-20 grid place-items-center text-slate-500">
                        <Loader2 size={28} className="animate-spin" />
                    </div>
                ) : quotes.length === 0 ? (
                    <div
                        data-testid="admin-empty"
                        className="border border-dashed border-navy p-16 text-center text-slate-500"
                    >
                        No quotes yet. Submissions will appear here.
                    </div>
                ) : view === "kanban" ? (
                    <div
                        data-testid="admin-kanban-board"
                        className="grid grid-cols-1 md:grid-cols-3 gap-5"
                    >
                        {LEAD_STATUSES.map((col) => {
                            const cards = sortedQuotes.filter(
                                (q) => (q.lead_status || "new") === col.key,
                            );
                            return (
                                <div
                                    key={col.key}
                                    data-testid={`kanban-column-${col.key}`}
                                    onDragOver={(e) => {
                                        e.preventDefault();
                                        setDragOverCol(col.key);
                                    }}
                                    onDragLeave={() =>
                                        setDragOverCol((c) =>
                                            c === col.key ? null : c,
                                        )
                                    }
                                    onDrop={() => handleColumnDrop(col.key)}
                                    className={`rounded-2xl border-t-2 border border-navy bg-navy/30 p-4 transition-colors ${
                                        KANBAN_COLUMN_ACCENT[col.key]
                                    } ${
                                        dragOverCol === col.key
                                            ? "bg-safety/10 border-safety/40"
                                            : ""
                                    }`}
                                >
                                    <div className="flex items-center justify-between mb-4">
                                        <span className="text-xs uppercase tracking-[0.2em] text-slate-300 font-semibold">
                                            {col.label}
                                        </span>
                                        <span
                                            data-testid={`kanban-count-${col.key}`}
                                            className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-xl border ${LEAD_BADGE_STYLES[col.key]}`}
                                        >
                                            {cards.length}
                                        </span>
                                    </div>
                                    <div className="space-y-3 min-h-[120px]">
                                        {cards.length === 0 ? (
                                            <div className="text-slate-600 text-xs text-center py-8 border border-dashed border-navy rounded-xl">
                                                Drop a lead here
                                            </div>
                                        ) : (
                                            cards.map((q) => (
                                                <KanbanCard
                                                    key={q.id}
                                                    q={q}
                                                    onView={openDetail}
                                                    onDragStart={handleDragStart}
                                                    onDragEnd={handleDragEnd}
                                                    dragging={draggedId === q.id}
                                                />
                                            ))
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="border border-navy">
                        <table
                            data-testid="admin-quotes-table"
                            className="w-full text-sm"
                        >
                            <thead>
                                <tr className="text-left text-xs uppercase tracking-[0.2em] text-slate-500 bg-navy border-b border-navy">
                                    <th className="p-4">Customer</th>
                                    <th className="p-4 hidden md:table-cell">
                                        Vehicle
                                    </th>
                                    <th className="p-4 hidden lg:table-cell">
                                        Services
                                    </th>
                                    <th className="p-4">AI Estimate</th>
                                    <th className="p-4 hidden md:table-cell">
                                        Submitted
                                    </th>
                                    <th className="p-4 w-16"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleQuotes.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan={6}
                                            data-testid="admin-no-match"
                                            className="p-10 text-center text-slate-500 font-light"
                                        >
                                            No {statusFilter} leads.
                                        </td>
                                    </tr>
                                ) : (
                                    visibleQuotes.map((q) => (
                                    <tr
                                        key={q.id}
                                        data-testid={`admin-row-${q.id}`}
                                        className="border-b border-navy hover:bg-navy/50 transition-colors"
                                    >
                                        <td className="p-4">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-slate-100 font-medium">
                                                    {q.full_name}
                                                </span>
                                                <span
                                                    data-testid={`admin-lead-status-badge-${q.id}`}
                                                    className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-xl border ${LEAD_BADGE_STYLES[q.lead_status] || LEAD_BADGE_STYLES.new}`}
                                                >
                                                    {leadLabel(q.lead_status || "new")}
                                                </span>
                                                {q.deposit_paid && (
                                                    <span
                                                        data-testid={`admin-booked-badge-${q.id}`}
                                                        className="text-[10px] uppercase tracking-[0.15em] text-emerald-400 border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 rounded-xl"
                                                    >
                                                        Booked
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-slate-500 text-xs mt-0.5">
                                                {q.email}
                                            </div>
                                        </td>
                                        <td className="p-4 hidden md:table-cell text-slate-300 font-light">
                                            {q.vehicle_year || ""}{" "}
                                            {q.vehicle_make} {q.vehicle_model}
                                            <div className="text-slate-500 text-xs capitalize">
                                                {q.vehicle_type}
                                            </div>
                                        </td>
                                        <td className="p-4 hidden lg:table-cell text-slate-300 font-light">
                                            {q.services
                                                .map(serviceLabel)
                                                .join(", ")}
                                        </td>
                                        <td className="p-4 text-safety">
                                            {formatPrice(q.ai_estimate_low || q.estimate_low)}
                                            <span className="text-slate-600">
                                                {" "}
                                                –{" "}
                                            </span>
                                            {formatPrice(q.ai_estimate_high || q.estimate_high)}
                                        </td>
                                        <td className="p-4 hidden md:table-cell text-slate-400 font-light">
                                            {new Date(
                                                q.created_at,
                                            ).toLocaleDateString()}
                                        </td>
                                        <td className="p-4">
                                            <button
                                                data-testid={`admin-view-${q.id}`}
                                                onClick={() => openDetail(q.id)}
                                                className="text-slate-400 hover:text-safety"
                                                aria-label="View details"
                                            >
                                                <Eye size={18} />
                                            </button>
                                        </td>
                                    </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
                </>)}
            </main>

            <Dialog
                open={!!active}
                onOpenChange={(o) => !o && setActive(null)}
            >
                <DialogContent
                    data-testid="admin-detail-dialog"
                    className="max-w-3xl bg-navy border-navy text-slate-100 max-h-[90vh] overflow-y-auto"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display text-3xl font-light">
                            Quote Details
                        </DialogTitle>
                    </DialogHeader>
                    {detailLoading || !active?.full_name ? (
                        <div className="py-12 grid place-items-center">
                            <Loader2
                                className="animate-spin text-slate-500"
                                size={28}
                            />
                        </div>
                    ) : (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <Info label="Name" value={active.full_name} />
                                <Info label="Email" value={active.email} />
                                <Info label="Phone" value={active.phone} />
                                <Info
                                    label="Location"
                                    value={
                                        active.suburb || active.region
                                            ? `${active.suburb || ""}${active.suburb && active.region ? ", " : ""}${active.region || ""}`
                                            : "—"
                                    }
                                />
                                <Info
                                    label="Vehicle"
                                    value={`${active.vehicle_year || ""} ${active.vehicle_make} ${active.vehicle_model}`}
                                />
                                <Info
                                    label="Type"
                                    value={active.vehicle_type}
                                />
                                <Info
                                    label="Damage size"
                                    value={active.damage_size}
                                />
                            </div>

                            {/* Lead pipeline status */}
                            <div
                                data-testid="admin-lead-status-block"
                                className="border border-navy bg-navy/40 rounded-xl p-4"
                            >
                                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">
                                    Lead status
                                </div>
                                <div className="inline-flex rounded-xl border border-navy overflow-hidden">
                                    {LEAD_STATUSES.map((s) => {
                                        const activeStatus =
                                            (active.lead_status || "new") === s.key;
                                        return (
                                            <button
                                                key={s.key}
                                                data-testid={`admin-lead-status-${s.key}`}
                                                onClick={() => changeLeadStatus(s.key)}
                                                disabled={savingStatus}
                                                className={`px-4 py-2 text-xs uppercase tracking-[0.15em] transition-colors disabled:opacity-50 ${
                                                    activeStatus
                                                        ? "bg-safety text-navy font-semibold"
                                                        : "text-slate-300 hover:bg-navy/60"
                                                }`}
                                            >
                                                {s.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Deposit & booking */}
                            <div
                                data-testid="admin-deposit-block"
                                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border border-navy bg-navy/40 rounded-xl p-4"
                            >
                                <div className="space-y-1">
                                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500">
                                        Deposit
                                    </div>
                                    {active.status === "refunded" ? (
                                        <div className="text-slate-400 text-sm">
                                            Refunded
                                        </div>
                                    ) : active.deposit_paid ? (
                                        <div className="text-emerald-400 text-sm font-medium">
                                            NZ$50 paid · Booked
                                        </div>
                                    ) : (
                                        <div className="text-slate-400 text-sm">
                                            Not paid
                                        </div>
                                    )}
                                    {active.dropoff_date && (
                                        <div className="flex items-center gap-2 text-safety text-sm pt-1">
                                            <CalendarCheck size={14} />
                                            Drop-off: {active.dropoff_date}
                                        </div>
                                    )}
                                </div>
                                {active.deposit_paid && (
                                    <button
                                        onClick={handleRefund}
                                        disabled={refunding}
                                        data-testid="admin-refund-button"
                                        className="shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.15em] text-slate-100 border border-red-500/50 hover:bg-red-500/15 hover:border-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded-xl"
                                    >
                                        {refunding ? (
                                            <Loader2
                                                size={14}
                                                className="animate-spin"
                                            />
                                        ) : (
                                            <RotateCcw size={14} />
                                        )}
                                        Refund NZ$50
                                    </button>
                                )}
                            </div>

                            <div>
                                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">
                                    Services
                                </div>
                                <div className="text-slate-200">
                                    {active.services
                                        .map(serviceLabel)
                                        .join(", ")}
                                </div>
                            </div>

                            {active.description && (
                                <div>
                                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">
                                        Customer notes
                                    </div>
                                    <div className="text-slate-300 font-light">
                                        {active.description}
                                    </div>
                                </div>
                            )}

                            {active.photo_base64 && (
                                <div>
                                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">
                                        Uploaded photo
                                    </div>
                                    <img
                                        data-testid="admin-detail-photo"
                                        src={`data:${active.photo_mime || "image/jpeg"};base64,${active.photo_base64}`}
                                        alt="Damage"
                                        className="w-full max-h-96 object-cover border border-navy"
                                    />
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-navy">
                                <div>
                                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">
                                        Rule-based range
                                    </div>
                                    <div className="font-display text-2xl">
                                        {formatPrice(active.estimate_low)} –{" "}
                                        {formatPrice(active.estimate_high)}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs uppercase tracking-[0.2em] text-safety mb-2">
                                        AI estimate
                                    </div>
                                    <div className="font-display text-2xl text-safety">
                                        {formatPrice(active.ai_estimate_low)} –{" "}
                                        {formatPrice(active.ai_estimate_high)}
                                    </div>
                                    <div className="text-xs text-slate-500 mt-1 uppercase tracking-[0.2em]">
                                        Confidence:{" "}
                                        {active.ai_confidence || "medium"}
                                    </div>
                                </div>
                            </div>

                            {active.ai_summary && (
                                <div>
                                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">
                                        AI summary
                                    </div>
                                    <p className="text-slate-200 font-light leading-relaxed">
                                        {active.ai_summary}
                                    </p>
                                </div>
                            )}

                            {active.ai_recommendations?.length > 0 && (
                                <div>
                                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">
                                        Recommendations
                                    </div>
                                    <ul className="space-y-2">
                                        {active.ai_recommendations.map(
                                            (r, i) => (
                                                <li
                                                    key={i}
                                                    className="flex gap-3 text-slate-300 font-light text-sm"
                                                >
                                                    <span className="text-safety">
                                                        ✦
                                                    </span>
                                                    <span>{r}</span>
                                                </li>
                                            ),
                                        )}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Info({ label, value }) {
    return (
        <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">
                {label}
            </div>
            <div className="text-slate-200 capitalize">{value || "—"}</div>
        </div>
    );
}

const SHOP_BADGE = {
    approved: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
    pending: "text-amber-300 border-amber-500/40 bg-amber-500/10",
    suspended: "text-rose-300 border-rose-500/40 bg-rose-500/10",
};

function ShopsPanel() {
    const [shops, setShops] = useState(null);
    const [busy, setBusy] = useState(null);

    const load = () => listShops().then(setShops).catch(() => setShops([]));
    useEffect(() => { load(); }, []);

    const change = async (id, status) => {
        setBusy(id);
        try {
            await setShopStatus(id, status);
            setShops((s) => s.map((x) => (x.id === id ? { ...x, status } : x)));
            toast.success(`Shop ${status}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not update shop");
        } finally { setBusy(null); }
    };

    if (shops === null) {
        return <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-safety" size={28} /></div>;
    }
    if (shops.length === 0) {
        return <div className="glass rounded-2xl p-10 text-center text-slate-500" data-testid="no-shops">No panel shops have registered yet.</div>;
    }
    return (
        <div className="border border-navy rounded-2xl overflow-hidden" data-testid="admin-shops-table">
            <table className="w-full text-left">
                <thead className="bg-navy/40 text-xs uppercase tracking-[0.15em] text-slate-400">
                    <tr>
                        <th className="p-4">Shop</th><th className="p-4">City</th>
                        <th className="p-4">Contact</th><th className="p-4">Status</th>
                        <th className="p-4 text-right">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {shops.map((s) => (
                        <tr key={s.id} data-testid={`shop-row-${s.id}`} className="border-t border-navy">
                            <td className="p-4">
                                <div className="text-slate-100 font-medium">{s.shop_name}</div>
                                <div className="text-slate-500 text-xs">{s.email}</div>
                            </td>
                            <td className="p-4 text-slate-300">{[s.city, s.region].filter(Boolean).join(", ") || "—"}</td>
                            <td className="p-4 text-slate-300 text-sm">{s.contact_name || "—"}<br /><span className="text-slate-500">{s.phone}</span></td>
                            <td className="p-4"><span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-xl border ${SHOP_BADGE[s.status] || SHOP_BADGE.pending}`}>{s.status}</span></td>
                            <td className="p-4">
                                <div className="flex gap-2 justify-end">
                                    {s.status !== "approved" && (
                                        <button data-testid={`shop-approve-${s.id}`} disabled={busy === s.id} onClick={() => change(s.id, "approved")}
                                            className="px-3 py-1.5 text-xs uppercase tracking-[0.12em] rounded-lg bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25 transition-colors disabled:opacity-50">Approve</button>
                                    )}
                                    {s.status !== "suspended" && (
                                        <button data-testid={`shop-suspend-${s.id}`} disabled={busy === s.id} onClick={() => change(s.id, "suspended")}
                                            className="px-3 py-1.5 text-xs uppercase tracking-[0.12em] rounded-lg bg-rose-500/10 text-rose-300 border border-rose-500/30 hover:bg-rose-500/20 transition-colors disabled:opacity-50">Suspend</button>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
